import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import CompanyService, { Entities } from "../../../services/CompanyService";
import {
  AutocompleteSelect,
  TagsDropdownEditor,
  TextEditor
} from "../../shared/components/Editors";
import {
  RequiredManager,
  ValidationMessage
} from "../../shared/RequieredManager";
import Dropdown from "../../shared/components/Dropdown";
import { EmailComposeModel, EmailTabProps } from "./EmailCompose";
import { EntityField } from "../../shared/entities/EntityField";
import EmailTemplateService from "../../../services/EmailTemplateService";
import { ToastService } from "../../shared/bootstrap/Toast";
import ModalService from "../../shared/bootstrap/modal/Modal";
import ActivityService, {
  EmailInvoiceListType
} from "../../../services/ActivityService";
import {
  addIf,
  hasValuesLength,
  isNullOrWhitespace
} from "../../../utils/Utils";
import ClientService from "../../../services/ClientService";
import { AutocompleteAsync } from "../../shared/bootstrap/AutocompleteAsync";
import { mapIfSuccess, handleError } from "../../../utils/RequestUtils";
import AccountService from "../../../services/AccountService";
import {
  EmailEditorTipTap,
  EmailEditorTipTapRef,
  formatHtmlLikeDraft
} from "./EmailEditorTipTap";
import GroupService from "../../../services/GroupService";
import { StorageService, CacheEntity } from "../../../services/StorageService";
import SwitchButton from "../../shared/components/SwitchButton";
import FilterService, {
  FilterCollection
} from "../../../services/FilterService";
import AdvancedFilters, {
  AdvancedFiltersContext
} from "../../shared/components/AdvancedFilters";
import InvoiceService, {
  InvoiceSetFields
} from "../../../services/InvoiceService";
import languages from "../../shared/entities/Languages";
import TooltipComponent from "../../shared/TooltipComponent";
import { v4 } from "uuid";
import MultiFileUploadNew from "../../shared/components/MultiFileUploadNew";
import Loading from "../../shared/components/Loading";
import { useInsertFieldOptions } from "../../../hooks/dropdowns/useInsertFieldOptions";

export const EmailTabMessage = ({
  model,
  setModel,
  getFeature,
  requiredManager,
  getCurrentEditorContent
}: EmailTabProps) => {
  const setEmail =
    <TKey extends keyof EmailComposeModel["item"]>(key: TKey) =>
    (value: EmailComposeModel["item"][TKey]) =>
      setModel((model) => {
        // Optimización: solo actualizar si el valor realmente cambió
        if (model.item?.[key] === value) {
          return model; // Retornar el mismo objeto si no hay cambios
        }
        const newModel = { ...model };
        if (!newModel.item) {
          newModel.item = {} as Partial<EmailComposeModel>["item"];
        }
        if (newModel.item) {
          newModel.item[key] = value;
        }
        return newModel;
      });

  const setTemp =
    <TKey extends keyof EmailComposeModel["temp"]>(key: TKey) =>
    (value: EmailComposeModel["temp"][TKey]) =>
      setModel((model) => {
        const newModel = { ...model };
        if (newModel.temp) {
          newModel.temp[key] = value;
        }
        return newModel;
      });

  const [withBCC, setWithBCC] = useState(Boolean(model.item?.bccto));
  const [withCC, setWithCC] = useState(Boolean(model.item?.ccto));
  const { translate } = TranslationService;
  const [isLoadingSignature, setIsLoadingSignature] = useState<boolean>(true);

  // Estado para controlar si el contenido anterior está colapsado
  const [isPreviousContentCollapsed, setIsPreviousContentCollapsed] =
    useState<boolean>(model.temp?.isPreviousContentCollapsed ?? false);

  const editorRef = useRef<EmailEditorTipTapRef | null>(null);
  // Ref para guardar el contenido con el comentario EMAIL_SEPARATOR_KEY
  const contentWithSeparatorRef = useRef<string | null>(null);
  // Ref para rastrear el último contenido procesado y evitar actualizaciones innecesarias
  const lastProcessedContentRef = useRef<string | null>(null);

  // Exponer función para obtener el contenido actual del editor
  // Usar useRef para mantener la referencia a la función actualizada
  const getCurrentContentRef = useRef<(() => string | undefined) | undefined>(
    undefined
  );

  useEffect(() => {
    getCurrentContentRef.current = () => {
      return editorRef.current?.getHTML() ?? model.item?.content ?? undefined;
    };
    // Actualizar la función externa si está disponible
    if (getCurrentEditorContent) {
      (getCurrentEditorContent as any).current = getCurrentContentRef.current;
    }
  }, [getCurrentEditorContent, model.item?.content]);

  const { show, setShow } = useContext(AdvancedFiltersContext);
  useEffect(() => {
    setShow(
      getFeature("includeinvoices").isOn &&
        model.item?.quickfilter === EmailInvoiceListType.Other
    );
  }, [getFeature, model.item?.quickfilter, setShow]);

  // Inicializar showPrevEmailBtn automáticamente cuando existe replytoid
  useEffect(() => {
    if (
      model.temp?.showPrevEmailBtn === undefined &&
      Number.isInteger(model.item?.replytoid)
    ) {
      setTemp("showPrevEmailBtn")(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.item?.replytoid, model.temp?.showPrevEmailBtn]);

  // Sincronizar estado de colapso desde el modelo cuando se carga contenido existente
  useEffect(() => {
    if (model.temp?.isPreviousContentCollapsed !== undefined) {
      setIsPreviousContentCollapsed(model.temp.isPreviousContentCollapsed);
    }
  }, [model.temp?.isPreviousContentCollapsed]);

  const emailTemplateOptions = useMemo(
    () =>
      CompanyService.getEmailTemplates().map((x) => ({
        value: x.MailTemplateID,
        text: x.Name
      })),
    []
  );

  const isIncludeInvoicesOn = getFeature("includeinvoices").isOn;
  const { data: insertFieldOptions } = useInsertFieldOptions({
    includeInvoices: isIncludeInvoicesOn
  });

  const activityTypeOptions = useMemo(
    () =>
      CompanyService.getActivityTypes()
        .filter((x) => x.Mail)
        .map((x) => ({ value: x.ActivityTypeID, text: x.ActivityTypeName })),
    []
  );

  const groupSelectOptions = async (inputValue: string) => {
    let list: Array<{ value: string; label: string }> = [];
    const minInputLength = 2;
    if (!model.item?.personid || inputValue.length < minInputLength) {
      return list;
    }
    const result = await GroupService.search(
      model.item!.personid.toString(),
      inputValue
    );
    if (!(result instanceof Error)) {
      list = result.list.map((x) => ({
        value: x.data.toString(),
        label: x.value
      }));
    }
    return list;
  };

  const onTemplateChange = async (templateId?: number) => {
    if (!templateId) {
      return;
    }
    const resultPromise = EmailTemplateService.get(templateId);
    const replaceContent = async () => {
      const result = await resultPromise;
      if (result instanceof Error) {
        ToastService.showToast(
          translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        return;
      }
      const template = result.item;
      let contentWithSignature = template.Content;

      setModel(
        (model) =>
          ({
            ...model,
            item: {
              ...model.item,
              bodyHtml: contentWithSignature,
              subject: template.Subject,
              activitytypeid: template.ActivityTypeID,
              activityTypeId: template.ActivityTypeID,
              files: template.files.map((x) => ({
                name: x.fileName,
                id: x.id
              })),
              templateattach: template.files.map((x) => parseInt(x.id)),
              templateId: templateId.toString(),
              LetterBlockNameID: template.FileLetter?.id,
              LetterBlockFileName: template.FileLetter?.fileName
            }
          }) as Partial<EmailComposeModel>
      );
      await updateEmailBody(contentWithSignature);

      // Después de cargar la plantilla, agregar la firma automáticamente
      // Esperar un poco para que el editor se actualice
      setIsLoadingSignature(true);
      setTimeout(async () => {
        try {
          if (!editorRef.current) {
            setIsLoadingSignature(false);
            return;
          }

          // Verificar si hay firma de imagen (si hay, no agregar firma de texto)
          const imageSignature = await handleError(
            AccountService.getAccountSignature()
          );
          if (imageSignature?.item && imageSignature.item.Content) {
            // Si hay firma de imagen, no agregar firma de texto
            setIsLoadingSignature(false);
            return;
          }

          // Obtener la firma de texto
          const textSignature = await getTextSignature();
          if (!textSignature) {
            setIsLoadingSignature(false);
            return;
          }

          // Obtener el contenido actual del editor
          const currentContent = editorRef.current.getHTML();

          // Verificar si hay contenido anterior (separador)
          const separatorIndex = currentContent.indexOf(
            "<!--EMAIL_SEPARATOR_KEY-->"
          );

          if (separatorIndex !== -1) {
            // Hay separador: insertar la firma justo antes del separador
            const contentBeforeSeparator = currentContent.substring(
              0,
              separatorIndex
            );
            const contentAfterSeparator =
              currentContent.substring(separatorIndex);
            const newContent =
              contentBeforeSeparator + textSignature + contentAfterSeparator;
            await updateEmailBody(newContent);
          } else {
            // No hay separador: insertar la firma al final
            await updateEmailBody(currentContent + textSignature);
          }
        } catch (error) {
          console.warn(
            "[EmailTabMessage] Error al cargar firma en plantilla:",
            error
          );
        } finally {
          setIsLoadingSignature(false);

          // Posicionar el cursor al inicio del editor cuando termine de cargar
          setTimeout(() => {
            if (editorRef.current) {
              editorRef.current.setCursorToStart();
              // Hacer focus automático para que aparezca el cursor
              editorRef.current.focus();
            }
          }, 150);
        }
      }, 500);
    };
    if (
      Boolean(model?.item?.content?.length) ||
      Boolean(model?.item?.subject?.length)
    ) {
      ModalService.showDefaultModal({
        title: TranslationService.translate.ReplaceContent,
        message: TranslationService.translate.ReplaceContentBody,
        acceptButtonLabel: translate.Yes,
        onAcceptClick: replaceContent
      });
    } else {
      replaceContent();
    }
  };

  const changeBody = requiredManager.makeRequiredWithId((x: string) => {
    setEmail("content")(x);
  }, "content");

  const updateEmailBody = async (content: string) => {
    changeBody(content);
    if (editorRef.current) {
      editorRef.current.setContent(content);
    }
  };

  // Función para obtener la firma de texto
  const getTextSignature = async (): Promise<string> => {
    try {
      const result = await handleError(
        AccountService.getAccountSignatureText()
      );
      if (!isNullOrWhitespace(result.signature)) {
        // Formatear la firma de texto como HTML válido para TipTap
        // Convertir saltos de línea en <br> y envolver en párrafos
        const signatureHtml = result.signature.replace(/\n/g, "<br>");

        // Crear HTML válido con párrafos y separador
        // Usar múltiples párrafos con <br> para los saltos de línea antes del separador
        // Esto asegura que TipTap pueda procesarlo correctamente
        return (
          "<p><br></p><p><br></p><p><br></p><p><br></p><p><br>" +
          signatureHtml +
          "</p>"
        );
      }
    } catch (error) {
      console.warn("[EmailTabMessage] Error al obtener firma de texto:", error);
    }
    return "";
  };

  // Agregar firma de texto automáticamente cuando se redacta un nuevo mail, respuesta o reenvío
  useEffect(() => {
    const addTextSignatureIfNeeded = async () => {
      // Iniciar carga de firma
      setIsLoadingSignature(true);

      try {
        // PRIMERO: Siempre hacer el get de la firma (imagen y texto) para verificar qué hay disponible
        // Esto debe hacerse independientemente del estado del editor
        const imageSignature = await handleError(
          AccountService.getAccountSignature()
        );
        const textSignature = await getTextSignature();

        // Si hay firma de imagen, no necesitamos agregar firma de texto
        if (imageSignature?.item && imageSignature.item.Content) {
          setIsLoadingSignature(false);
          return;
        }

        // Esperar a que el editor esté disponible antes de verificar contenido o insertar firma
        // Intentar varias veces con un pequeño delay
        let attempts = 0;
        const maxAttempts = 20; // 20 intentos * 100ms = máximo 2 segundos
        while (!editorRef.current && attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          attempts++;
        }

        if (!editorRef.current) {
          setIsLoadingSignature(false);
          return;
        }

        const currentContent = editorRef.current.getHTML();

        // Extraer solo el contenido nuevo (antes del separador EMAIL_SEPARATOR_KEY si existe)
        let newContentOnly = currentContent;
        const separatorIndex = currentContent.indexOf(
          "<!--EMAIL_SEPARATOR_KEY-->"
        );
        if (separatorIndex !== -1) {
          newContentOnly = currentContent.substring(0, separatorIndex);
        }

        // Verificar si el contenido nuevo está vacío (sin contenido o solo párrafos vacíos)
        const isEmpty =
          !newContentOnly ||
          newContentOnly.trim() === "" ||
          newContentOnly === "<p></p>" ||
          newContentOnly === "<p><br></p>" ||
          newContentOnly.trim() === "<p></p>";

        if (!isEmpty) {
          setIsLoadingSignature(false);
          return;
        }

        // Si no hay firma de texto, no hay nada que agregar
        if (!textSignature) {
          setIsLoadingSignature(false);
          return;
        }

        // Obtener y agregar firma de texto
        if (textSignature && editorRef.current) {
          // Verificar nuevamente el contenido actual del editor
          const finalContent = editorRef.current.getHTML();
          const finalSeparatorIndex = finalContent.indexOf(
            "<!--EMAIL_SEPARATOR_KEY-->"
          );
          let finalNewContentOnly = finalContent;
          if (finalSeparatorIndex !== -1) {
            finalNewContentOnly = finalContent.substring(
              0,
              finalSeparatorIndex
            );
          }

          const isStillEmpty =
            !finalNewContentOnly ||
            finalNewContentOnly.trim() === "" ||
            finalNewContentOnly === "<p></p>" ||
            finalNewContentOnly === "<p><br></p>";

          if (isStillEmpty) {
            // Si está vacío y no hay separador, usar setContent para establecer el contenido completo
            if (finalSeparatorIndex === -1) {
              await updateEmailBody(textSignature);
            } else {
              // Si hay separador, insertar la firma justo antes del separador
              const contentBeforeSeparator = finalContent.substring(
                0,
                finalSeparatorIndex
              );
              const contentAfterSeparator =
                finalContent.substring(finalSeparatorIndex);
              const newContent =
                contentBeforeSeparator + textSignature + contentAfterSeparator;
              await updateEmailBody(newContent);
            }
          } else {
            // Si tiene contenido nuevo, insertar la firma al final del contenido nuevo (antes del separador si existe)
            if (finalSeparatorIndex !== -1) {
              // Hay separador: insertar antes del separador
              const contentBeforeSeparator = finalContent.substring(
                0,
                finalSeparatorIndex
              );
              const contentAfterSeparator =
                finalContent.substring(finalSeparatorIndex);
              const newContent =
                contentBeforeSeparator + textSignature + contentAfterSeparator;
              await updateEmailBody(newContent);
            } else {
              // No hay separador: insertar al final
              editorRef.current.insertContent(textSignature);
            }
          }
        }
      } catch (error) {
        console.warn("[EmailTabMessage] Error al cargar firma:", error);
      } finally {
        // Finalizar carga de firma
        setIsLoadingSignature(false);

        // Posicionar el cursor al inicio del editor cuando termine de cargar
        // Esperar un poco para que el editor termine de renderizar completamente
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.setCursorToStart();
            // Hacer focus automático para que aparezca el cursor
            editorRef.current.focus();
          }
        }, 150);
      }
    };

    // Ejecutar para nuevos mails, respuestas y reenvíos
    // Ejecutar después de un delay para asegurar que el editor esté completamente inicializado
    const timeoutId = setTimeout(() => {
      addTextSignatureIfNeeded();
    }, 800);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.item?.replytoid]);

  // Ref para rastrear si ya se hizo focus inicial para este email
  const hasInitialFocusRef = useRef<boolean>(false);
  // Ref para rastrear el último replytoid para resetear el flag cuando cambie
  const lastReplyToIdRef = useRef<number | undefined>(undefined);

  // Resetear el flag de focus cuando cambia el email (replytoid)
  useEffect(() => {
    if (model.item?.replytoid !== lastReplyToIdRef.current) {
      hasInitialFocusRef.current = false;
      lastReplyToIdRef.current = model.item?.replytoid;
    }
  }, [model.item?.replytoid]);

  // Efecto para hacer focus automático cuando termine de cargar todo
  useEffect(() => {
    // Solo hacer focus si no está cargando la firma y el editor está disponible
    if (
      !isLoadingSignature &&
      editorRef.current &&
      !hasInitialFocusRef.current
    ) {
      // Esperar un poco más para asegurar que todo el DOM esté completamente renderizado
      const focusTimeout = setTimeout(() => {
        if (editorRef.current) {
          // Primero posicionar el cursor al inicio
          editorRef.current.setCursorToStart();
          // Luego hacer focus para que aparezca el cursor
          editorRef.current.focus();
          // Marcar que ya se hizo el focus inicial
          hasInitialFocusRef.current = true;
        }
      }, 300);

      return () => {
        clearTimeout(focusTimeout);
      };
    }
  }, [isLoadingSignature]);

  const insertTextInEditor = async (text?: string) => {
    if (!text || !editorRef.current) return;
    editorRef.current.insertContent(text);
    // El onChange del editor se encargará de actualizar el modelo
  };

  const loadPrevMail = async () => {
    if (!model.temp?.showPrevEmailBtn) {
      return model.item?.content ?? "";
    }

    setTemp("showPrevEmailBtn")("loading");
    const result = await ActivityService.getEmailContent({
      id: model!.item!.replytoid!.toString(),
      noheader: "0"
    });

    if (result instanceof Error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setTemp("showPrevEmailBtn")(true);
      return undefined;
    }

    setTemp("showPrevEmailBtn")(false);
    setEmail("replacebody")("0");

    const separatorComment = `<!--EMAIL_SEPARATOR_KEY-->`;

    const base = model.item?.content ? model.item?.content : "<br/>";

    // Reemplazar todos los <hr> (con cualquier atributo) por __ en el contenido anterior
    // Esto incluye: <hr>, <hr/>, <hr />, <hr contenteditable="false">, <hr contenteditable="false" class="">, etc.
    const normalizedPreviousContent = result.content.replace(
      /<hr[^>]*>/gi,
      "__"
    );

    // IMPORTANTE: Formatear el contenido anterior para asegurar que tenga \n después de cada </p>
    // Esto es necesario porque TipTap normaliza el HTML y puede perder los \n que venían del backend
    // cuando se carga el contenido anterior del email
    const formattedPreviousContent = formatHtmlLikeDraft(
      normalizedPreviousContent
    );

    // Envolver el contenido anterior en un div con clase para facilitar el colapso
    const previousContentWrapped = `<div class="email-previous-content-wrapper email-previous-content-expanded">${formattedPreviousContent}</div>`;
    // Insertar la clave ANTES del separador (usando __ en lugar de <hr />)
    const newEmailBody =
      base + separatorComment + "__" + previousContentWrapped;

    // Guardar el contenido con el comentario en el ref
    contentWithSeparatorRef.current = newEmailBody;

    // IMPORTANTE: Marcar isExpanded ANTES de updateEmailBody para que onEditorStateChange pueda detectarlo
    setTemp("isExpanded")(true);

    updateEmailBody(newEmailBody);

    // Inicializar el estado de colapso como false (expandido) cuando se carga el contenido
    setIsPreviousContentCollapsed(false);
    setTemp("isPreviousContentCollapsed")(false);

    return newEmailBody;
  };

  // Verificar si hay contenido anterior en el editor
  const hasPreviousContent = useMemo(() => {
    const content = model.item?.content ?? "";
    // Buscar tanto __ como <hr> (con o sin atributos) para compatibilidad
    return content.includes("__") || /<hr[^>]*>/i.test(content);
  }, [model.item?.content]);

  // Ref para rastrear el último estado de colapso aplicado
  const lastCollapsedStateRef = useRef<boolean | null>(null);

  // Efecto para aplicar estilos de colapso solo cuando cambia el estado de colapso o se carga contenido anterior
  useEffect(() => {
    if (!hasPreviousContent || !editorRef.current) {
      lastCollapsedStateRef.current = null;
      return;
    }

    // Solo aplicar estilos si el estado de colapso cambió o es la primera vez
    if (lastCollapsedStateRef.current === isPreviousContentCollapsed) {
      return;
    }

    const applyStyles = () => {
      const editorElement = document.querySelector(
        ".email-editor-content .ProseMirror"
      );
      if (editorElement) {
        const hrElements = editorElement.querySelectorAll("hr");
        hrElements.forEach((hr) => {
          let nextElement = hr.nextElementSibling;
          while (nextElement) {
            if (
              nextElement.classList &&
              nextElement.classList.contains("email-previous-content-wrapper")
            ) {
              if (isPreviousContentCollapsed) {
                nextElement.classList.remove("email-previous-content-expanded");
                nextElement.classList.add("email-previous-content-collapsed");
              } else {
                nextElement.classList.remove(
                  "email-previous-content-collapsed"
                );
                nextElement.classList.add("email-previous-content-expanded");
              }
              break;
            }
            if (nextElement.tagName === "HR") break;
            nextElement = nextElement.nextElementSibling;
          }
        });
        lastCollapsedStateRef.current = isPreviousContentCollapsed;
      }
    };

    // Usar requestAnimationFrame para aplicar estilos sin bloquear el render ni perder el foco
    requestAnimationFrame(() => {
      requestAnimationFrame(applyStyles);
    });
  }, [hasPreviousContent, isPreviousContentCollapsed]);

  const [sortByItems, setSortByItems] = useState(getDefaultSortByItems(model));

  const deleteSortBy = (index: number) => {
    if (index === 0) {
      getFeature(orderByFeatureName).setFeatureValue("orderby0")(
        model?.item?.orderby1
      );
      getFeature(orderByFeatureName).setFeatureValue("orderbyorderdesc0")(
        model?.item?.orderbyorderdesc1 ?? "off"
      );
    }
    if (index <= 1) {
      getFeature(orderByFeatureName).setFeatureValue("orderby1")(
        model?.item?.orderby2
      );
      getFeature(orderByFeatureName).setFeatureValue("orderbyorderdesc1")(
        model?.item?.orderbyorderdesc2 ?? "off"
      );
    }
    getFeature(orderByFeatureName).setFeatureValue("orderby2")(undefined);
    getFeature(orderByFeatureName).setFeatureValue("orderbyorderdesc2")("off");
    setSortByItems((x) => {
      x.splice(index, 1);
      return x.length === 0
        ? [{ orderBy: undefined, orderbyorderdesc: "off", _key: v4() }]
        : [...x];
    });
  };

  const groupByFields = useMemo(
    () =>
      [
        ...CompanyService.getAdditionalDefinitions()
          .filter((x) => !x.ExcludeInFilters && x.Entity === Entities.Invoice)
          .map((x) => new EntityField(x))
          .map((x) => ({ value: x.id, text: x.title ?? "" })),
        ...(CompanyService.getGroupName()
          ? [{ value: -1007, text: CompanyService.getGroupName() }]
          : []),
        {
          value: InvoiceSetFields.status,
          text: TranslationService.translate.Status
        }, //-1010
        {
          value: InvoiceSetFields.emittedDate,
          text: TranslationService.translate.IssueDate
        }, //-1023
        {
          value: InvoiceSetFields.dueDate,
          text: TranslationService.translate.DueDate
        }, //-1024
        {
          value: InvoiceSetFields.referenceNumber,
          text: InvoiceService.getReferenceNumberName()
        }, //-1009
        {
          value: InvoiceSetFields.amount,
          text: TranslationService.translate.Amount
        }, //-1011
        {
          value: InvoiceSetFields.pendingAmount,
          text: TranslationService.translate.PendingAmount
        }, //-1012
        {
          value: InvoiceSetFields.ioStatus,
          text: TranslationService.translate.InvoiceStatus
        }, //-1015
        {
          value: InvoiceSetFields.currency,
          text: TranslationService.translate.Currency
        }, //-1017
        {
          value: InvoiceSetFields.groupName,
          text: TranslationService.translate.Group
        } //-1008
      ].sort((x, y) => x.text.localeCompare(y.text)),
    []
  );

  const orderByFields = useMemo(() => [...groupByFields], [groupByFields]);

  const mailGroupByFields = useMemo(
    () =>
      [
        ...CompanyService.getAdditionalDefinitions()
          .filter((x) => !x.ExcludeInFilters && x.Entity === Entities.Invoice)
          .map((x) => new EntityField(x))
          .map((x) => ({ value: x.id, text: x.title ?? "" })),
        ...(CompanyService.getSetting("showgrouptable")
          ? [
              {
                value: InvoiceSetFields.groupId,
                text: CompanyService.getGroupName()
              }
            ]
          : []),
        {
          value: InvoiceSetFields.ioStatus,
          text: TranslationService.translate.InvoiceStatus
        }, //-1015
        {
          value: InvoiceSetFields.currency,
          text: TranslationService.translate.Currency
        } //-1017
      ].sort((x, y) => x.text.localeCompare(y.text)),
    []
  );

  const orderByFeatureName = "orderby0";
  const addresBarProps = { model, setEmail };

  const invoiceReportExportFiltered = useMemo(
    () =>
      CompanyService.getReportExports().filter(
        (x) => x.Report === Entities.Invoice && x.Public
      ),
    []
  );
  const invoiceReportExportOptions = useMemo(
    () =>
      invoiceReportExportFiltered.map((x) => ({
        value: x.ReportExportID,
        text: x.Name ?? TranslationService.translate.Default
      })),
    [invoiceReportExportFiltered]
  );
  const defaultReportExportOption = useMemo(
    () => invoiceReportExportFiltered.find((x) => x?.Default)?.ReportExportID,
    [invoiceReportExportFiltered]
  );

  const invoiceListModeOptions: { value: "0" | "1"; text: string }[] = useMemo(
    () => [
      { value: "1", text: TranslationService.translate.EmailOnBody },
      { value: "0", text: TranslationService.translate.EmailAsAttachment }
    ],
    []
  );

  const invoiceListTypeOptions = useMemo(
    () => [
      // { value: EmailInvoiceListType.All, text: TranslationService.translate.AllInvoices },
      {
        value: EmailInvoiceListType.Pending,
        text: TranslationService.translate.AllPending
      },
      {
        value: EmailInvoiceListType.Due,
        text: TranslationService.translate.OnlyDues
      },
      {
        value: EmailInvoiceListType.Claimables,
        text: TranslationService.translate.OnlyClaimable
      },
      {
        value: EmailInvoiceListType.DueClaimables,
        text: TranslationService.translate.OnlyDuesClaimable
      },
      {
        value: EmailInvoiceListType.Other,
        text: TranslationService.translate.AdvancedFilters
      },
      ...addIf(Boolean(model.item?.ids?.length), {
        value: EmailInvoiceListType.Selected,
        text: TranslationService.translate.SelectedInvoices.replace(
          "{invoiceCount}",
          model.item?.ids?.split(",").length.toString() ?? "0"
        )
      })
    ],
    [model.item]
  );

  const applyAdvancedFilter = (filters: string[] | undefined) => {
    setEmail("filter")(FilterService.GetExtraFiltersRequestString(filters));
  };
  const emailFrameOptions = useMemo(
    () =>
      CompanyService.getEmailFrames().map((x) => ({
        value: x.MailFrameID,
        text: x.Name
      })),
    []
  );
  const groupLabel =
    model.item?.groupid &&
    StorageService.getCache(
      CacheEntity.GroupName,
      model.item?.groupid.toString()
    );

  const handlePastedFiles = async (files: File[]) => {
    files.forEach((file) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        if (editorRef.current) {
          editorRef.current.chain()?.focus().setImage({ src: base64 }).run();
        }
      };
    });
  };

  return (
    <>
      <div className="tabBody">
        <div className="row mb-2 g-0 align-items-center">
          <div className="col-1">
            <label className="form-label mb-0">
              {TranslationService.translate.From}
            </label>
          </div>
          <div className="col row px-0">
            <div className="col-6 pe-2 d-flex align-items-center">
              {CompanyService.getSetting("allowemailfromedit") ? (
                <input
                  className="form-control"
                  defaultValue={model.item?.fromname}
                  readOnly={!CompanyService.getSetting("allowemailfromedit")}
                  onChange={(e) => setEmail("fromname")(e.target.value)}
                  placeholder={TranslationService.translate.Name}
                />
              ) : (
                <p className="text-center">
                  {model.item?.fromname} {`<${model.item?.from}>`}
                </p>
              )}
            </div>
            <div className="col-6 ps-2">
              <div className="d-flex justify-content-end">
                {CompanyService.getSetting("allowemailfromedit") && (
                  <input
                    className="form-control"
                    defaultValue={model.item?.from}
                    readOnly={!CompanyService.getSetting("allowemailfromedit")}
                    onChange={(e) => setEmail("from")(e.target.value)}
                    placeholder={TranslationService.translate.Email}
                  />
                )}
                <div className="d-flex flex-nowrap gap-2">
                  {!withCC && (
                    <button
                      className="btn btn-link"
                      type="button"
                      onClick={() => setWithCC(true)}
                    >
                      CC
                    </button>
                  )}
                  {!withBCC && (
                    <button
                      className="btn btn-link"
                      type="button"
                      onClick={() => setWithBCC(true)}
                    >
                      {TranslationService.translate.EmailBCC}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <AddressBar
          {...addresBarProps}
          emailProp={"to"}
          requiredManager={requiredManager}
          label={TranslationService.translate.EmailTo}
        />
        {withCC && (
          <AddressBar {...addresBarProps} emailProp={"ccto"} label={"CC"} />
        )}
        {withBCC && (
          <AddressBar
            {...addresBarProps}
            emailProp={"bccto"}
            label={TranslationService.translate.EmailBCC}
          />
        )}
        <AddressBar
          {...addresBarProps}
          emailProp={"subject"}
          requiredManager={requiredManager}
          label={TranslationService.translate.Subject}
        />
        <hr />
        <div className="row mb-4">
          {hasValuesLength(emailTemplateOptions) && (
            <div className="col-6">
              <Dropdown
                onChange={onTemplateChange}
                items={emailTemplateOptions}
                optionLabel={TranslationService.translate.SelectTemplate}
              />
            </div>
          )}
          {hasValuesLength([insertFieldOptions]) && (
            <div className="col-6">
              <Dropdown
                onChange={insertTextInEditor}
                items={insertFieldOptions}
                value={undefined}
                placeholder={TranslationService.translate.EmailInsertField}
              />
            </div>
          )}
        </div>
        <div
          className={
            "row g-0 mb-3" +
            (model.temp?.showPrevEmailBtn
              ? " mce-editor-prev-message-margin"
              : "")
          }
          style={{ position: "relative" }}
        >
          {isLoadingSignature && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Loading height={300} />
            </div>
          )}
          <EmailEditorTipTap
            ref={editorRef}
            content={model.item?.content ?? ""}
            onChange={(markup) => {
              // Guardar el markup original antes de cualquier modificación
              const originalMarkup = markup;

              // Normalizar: reemplazar <hr> (con o sin atributos) por __ para simplificar el procesamiento
              markup = markup.replace(/<hr[^>]*>/gi, "__");

              // Preservar el comentario EMAIL_SEPARATOR_KEY si el usuario expandió el contenido
              // Primero intentar usar el ref si está disponible
              if (
                contentWithSeparatorRef.current &&
                !markup.includes("<!--EMAIL_SEPARATOR_KEY-->")
              ) {
                const refContent = contentWithSeparatorRef.current;
                const commentIndex = refContent.indexOf(
                  "<!--EMAIL_SEPARATOR_KEY-->"
                );

                if (commentIndex !== -1) {
                  // Extraer el nuevo texto del markup actual (puede haber cambiado por edición)
                  // Y el contenido anterior del ref (que no debería cambiar)
                  const previousContentFromRef = refContent.substring(
                    commentIndex + "<!--EMAIL_SEPARATOR_KEY-->".length
                  );

                  // Buscar el separador __ que indica dónde empieza el contenido anterior
                  // Normalizar el ref también para que use __
                  const normalizedRef = previousContentFromRef.replace(
                    /<hr[^>]*>/gi,
                    "__"
                  );

                  let insertIndex = -1;

                  // Buscar el wrapper primero (más específico)
                  const wrapperIndex = markup.indexOf(
                    'class="email-previous-content-wrapper'
                  );
                  if (wrapperIndex !== -1) {
                    insertIndex = wrapperIndex;
                    console.log(
                      "[onChange] Encontrado wrapper en índice:",
                      insertIndex
                    );
                  } else {
                    // Buscar el separador __ que precede al contenido del email anterior
                    // Buscar todos los __ en el markup
                    const allSeparatorMatches: number[] = [];
                    let searchIndex = 0;
                    while (true) {
                      const sepPos = markup.indexOf("__", searchIndex);
                      if (sepPos === -1) break;
                      allSeparatorMatches.push(sepPos);
                      searchIndex = sepPos + 2;
                    }

                    // Buscar el patrón de email después de cada separador
                    const emailStartPattern =
                      /<[^>]*>.*?(?:De:|Enviado:|Asunto:|Para:)/i;

                    // Para cada separador, verificar si después viene el contenido del email anterior
                    for (const sepPos of allSeparatorMatches) {
                      const afterSep = markup.substring(sepPos + 2); // +2 para saltar "__"
                      const emailMatch = afterSep.match(emailStartPattern);

                      if (emailMatch && emailMatch.index !== undefined) {
                        const emailStartInMarkup =
                          sepPos + 2 + emailMatch.index;
                        // Verificar si este email coincide con el inicio del contenido anterior del ref
                        const emailStartInRef =
                          normalizedRef.match(emailStartPattern);
                        if (
                          emailStartInRef &&
                          emailStartInRef.index !== undefined
                        ) {
                          // Comparar los primeros caracteres del email
                          const emailInMarkup = markup.substring(
                            emailStartInMarkup,
                            emailStartInMarkup + 100
                          );
                          const emailInRef = normalizedRef.substring(
                            emailStartInRef.index,
                            emailStartInRef.index + 100
                          );

                          // Si coinciden, este es el separador correcto
                          // eslint-disable-next-line no-mixed-operators
                          if (
                            emailInMarkup.replace(/\s+/g, " ") ===
                              emailInRef.replace(/\s+/g, " ") ||
                            // eslint-disable-next-line no-mixed-operators
                            (emailInMarkup.includes("De:") &&
                              emailInRef.includes("De:"))
                          ) {
                            insertIndex = sepPos;
                            break;
                          }
                        }
                      }
                    }

                    // Si no encontramos el separador correcto, usar el último __ antes del primer patrón de email
                    if (insertIndex === -1 && allSeparatorMatches.length > 0) {
                      const emailMatch = markup.match(emailStartPattern);
                      if (emailMatch && emailMatch.index !== undefined) {
                        const emailStartIndex = emailMatch.index;
                        for (
                          let i = allSeparatorMatches.length - 1;
                          i >= 0;
                          i--
                        ) {
                          if (allSeparatorMatches[i] < emailStartIndex) {
                            insertIndex = allSeparatorMatches[i];
                            console.log(
                              "[onChange] Usando último separador __ antes del primer email en índice:",
                              insertIndex
                            );
                            break;
                          }
                        }
                      }
                    }
                  }

                  if (insertIndex !== -1) {
                    const newText = markup.substring(0, insertIndex).trim();
                    markup =
                      newText +
                      "<!--EMAIL_SEPARATOR_KEY-->" +
                      previousContentFromRef;
                  } else {
                    console.warn(
                      "[onChange] No se pudo encontrar punto de inserción para restaurar desde ref"
                    );
                  }
                }
              }

              // Preservar el comentario EMAIL_SEPARATOR_KEY si el usuario expandió el contenido
              // y el comentario se perdió durante la edición
              if (
                model.temp?.isExpanded === true &&
                !markup.includes("<!--EMAIL_SEPARATOR_KEY-->")
              ) {
                // Buscar el div con la clase email-previous-content-wrapper o el separador __
                const wrapperIndex = markup.indexOf(
                  'class="email-previous-content-wrapper'
                );
                const separatorIndex = markup.indexOf("__");

                // Determinar dónde insertar el comentario
                let insertIndex = -1;
                if (wrapperIndex !== -1) {
                  // Insertar antes del div wrapper
                  insertIndex = wrapperIndex;
                } else if (separatorIndex !== -1) {
                  // Insertar antes del separador __
                  insertIndex = separatorIndex;
                } else {
                  // Si no encontramos marcadores, buscar el contenido del email anterior
                  // que generalmente tiene "De:" o "Enviado:" al inicio
                  const emailStartPattern =
                    /<[^>]*>.*?(?:De:|Enviado:|Asunto:|Para:)/i;
                  const match = markup.match(emailStartPattern);
                  if (match && match.index !== undefined) {
                    insertIndex = match.index;
                  }
                }

                if (insertIndex !== -1) {
                  // Extraer el nuevo texto (todo antes del punto de inserción)
                  const newText = markup.substring(0, insertIndex).trim();
                  const previousContent = markup.substring(insertIndex);

                  // Reconstruir con el comentario
                  markup =
                    newText + "<!--EMAIL_SEPARATOR_KEY-->" + previousContent;

                  // Actualizar el ref con el contenido que incluye el comentario
                  contentWithSeparatorRef.current = markup;
                } else {
                  console.warn(
                    "[onChange] No se pudo encontrar punto de inserción para el comentario"
                  );
                }
              } else if (
                model.temp?.isExpanded === true &&
                markup.includes("<!--EMAIL_SEPARATOR_KEY-->")
              ) {
                // Si el comentario está presente, actualizar el ref
                contentWithSeparatorRef.current = markup;
              }

              // Solo actualizar si el contenido realmente cambió
              // Comparar el markup final (después de todas las modificaciones) con el último procesado
              const hasChanged = lastProcessedContentRef.current !== markup;

              if (hasChanged) {
                lastProcessedContentRef.current = markup;
                changeBody(markup);
              } else {
                console.log(
                  "[onChange] ❌ NO se actualiza - contenido igual al anterior después de procesamiento"
                );
                console.log(
                  "[onChange] Markup original:",
                  originalMarkup.substring(0, 100)
                );
                console.log(
                  "[onChange] Markup procesado:",
                  markup.substring(0, 100)
                );
              }
            }}
            onPasteFiles={handlePastedFiles}
          />
          {model.temp?.showPrevEmailBtn && (
            <div style={{ height: 0, position: "relative" }}>
              {model.temp?.showPrevEmailBtn === "loading" ? (
                <button
                  className="btn btn-sm btn-secondary email-ellipsis-dots"
                  style={{ padding: 0, paddingLeft: 10, paddingRight: 20 }}
                  disabled
                >
                  <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-secondary email-ellipsis-dots"
                  onClick={loadPrevMail}
                  title={
                    translate.ShowPreviousMessage || "Mostrar mensaje anterior"
                  }
                >
                  <i className="fas fa-ellipsis-h"></i>
                </button>
              )}
            </div>
          )}
          <ValidationMessage
            onChange={requiredManager.getValidation("content")}
            defaultValue={model.item?.content}
          />
        </div>
        <div className="row mb-3 g-0 align-items-center">
          <div className="col-3 py-1">
            <SwitchButton
              label={TranslationService.translate.IncludeInvoices}
              onChange={(x) => {
                getFeature("includeinvoices").toggleFeature(x);
                getFeature("includeinvoices").setFeatureValue(
                  "includeinvoices"
                )(x);
              }}
              defaultValue={getFeature("includeinvoices").isOn}
            />
          </div>
          {getFeature("includeinvoices").isOn && (
            <>
              <div className="col-6">
                <div className="row">
                  <div className="col">
                    <Dropdown
                      onChange={(x) => {
                        getFeature("includeinvoices").setFeatureValue(
                          "quickfilter"
                        )(x);
                        setShow(x === EmailInvoiceListType.Other);
                      }}
                      items={invoiceListTypeOptions}
                      value={model.item?.quickfilter ?? null}
                      readonly={!getFeature("includeinvoices").isOn}
                    />
                  </div>
                  <div className="col">
                    <Dropdown
                      onChange={getFeature("includeinvoices").setFeatureValue(
                        "listmode"
                      )}
                      items={invoiceListModeOptions}
                      defaultValue={model.item?.listmode}
                      readonly={!getFeature("includeinvoices").isOn}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div
            className="col-12"
            style={{ marginTop: 12, marginBottom: show ? 0 : -12 }}
          >
            <AdvancedFilters
              autoApply
              defaultValue={model.item?.filter ?? undefined}
              page={FilterCollection.Invoice}
              onFilterApply={applyAdvancedFilter}
              allowClosing={false}
              removeApplyButtton
            />
          </div>
          <div className="col-12 py-1">
            <SwitchButton
              label={TranslationService.translate.AddLinkPortal}
              onChange={setEmail("includelink")}
              defaultValue={Boolean(model?.item?.includelink)}
            />
          </div>
          <div className="col-12 py-1">
            <SwitchButton
              label={TranslationService.translate.IncludeAttachment}
              onChange={setEmail("includeattachment")}
              defaultValue={Boolean(model?.item?.includeattachment)}
            />
          </div>
          <div className="col-auto py-1 me-5">
            <SwitchButton
              label={TranslationService.translate.EmailDesign}
              defaultValue={getFeature("mailframeid").isOn}
              onChange={getFeature("mailframeid").toggleFeature}
            />
          </div>
          <div className="col-4">
            {getFeature("mailframeid").isOn && (
              <div className="row">
                <div className="col">
                  <Dropdown
                    onChange={getFeature("mailframeid").setFeatureValue(
                      "mailframeid"
                    )}
                    items={emailFrameOptions}
                    optionLabel={TranslationService.translate.None}
                    defaultValue={model.item?.mailframeid}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="pt-1 ps-1">
            <MultiFileUploadNew
              files={model.item?.files}
              onFilesChange={setEmail("files")}
            />
          </div>
          {model.item?.LetterBlockNameID && (
            <div className="col-12 pt-2 ps-1">
              <div className="d-flex align-items-center gap-2">
                <div style={{ width: 45, textAlign: "center" }}>
                  <TooltipComponent
                    title={
                      TranslationService.translate
                        .AutomaticLetterTemplateTooltip
                    }
                  >
                    <i className="far fa-envelope activity-icon-color my-2" />
                  </TooltipComponent>
                </div>
                <div className="col d-flex align-items-center gap-2">
                  <span className="text-muted small">
                    {(model.item as any)?.LetterBlockFileName ||
                      TranslationService.translate.UploadWordTemplate}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setEmail("LetterBlockNameID")(undefined);
                      setModel((model) => {
                        const newModel = { ...model };
                        if (newModel.item) {
                          (newModel.item as any).LetterBlockFileName =
                            undefined;
                        }
                        return newModel;
                      });
                    }}
                    className="btn btn-link p-0 m-0"
                    style={{ paddingLeft: 4, paddingRight: 4 }}
                    aria-label={TranslationService.translate.Delete}
                    title={TranslationService.translate.Delete}
                  >
                    <svg
                      height="14"
                      width="14"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
                        fill="#0076b6"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <hr />
        {getFeature("includeinvoices").isOn && (
          <div className="row mb-3 g-0 align-items-center justify-content-between">
            <h5 className="mb-2">
              {TranslationService.translate.InvoiceTableOptions}
            </h5>
            {CompanyService.getSetting("alloweditgroupmailby") && (
              <>
                <div className="col-3 py-1">
                  <SwitchButton
                    label={TranslationService.translate.GroupInvoices}
                    onChange={(x) =>
                      setTimeout(
                        () => getFeature("mail_groupby").toggleFeature(x),
                        5
                      )
                    }
                    defaultValue={getFeature("mail_groupby").isOn}
                    disabled={!getFeature("includeinvoices").isOn}
                  />
                </div>
                <div className="col-8">
                  {getFeature("mail_groupby").isOn &&
                    getFeature("includeinvoices").isOn && (
                      <Dropdown
                        onChange={getFeature("mail_groupby").setFeatureValue(
                          "mail_groupby"
                        )}
                        items={mailGroupByFields}
                        optionLabel={TranslationService.translate.Select}
                        defaultValue={model.item?.mail_groupby}
                        readonly={
                          !getFeature("mail_groupby").isOn ||
                          !getFeature("includeinvoices").isOn
                        }
                      />
                    )}
                </div>
              </>
            )}
            <div
              className={
                getFeature(orderByFeatureName).isOn
                  ? "col-3 py-1 mb-2"
                  : "col-12 py-1  mb-2"
              }
            >
              <SwitchButton
                label={TranslationService.translate.SortInvoices}
                onChange={(x) =>
                  setTimeout(
                    () => getFeature(orderByFeatureName).toggleFeature(x),
                    5
                  )
                }
                defaultValue={getFeature(orderByFeatureName).isOn}
                disabled={!getFeature("includeinvoices").isOn}
              />
            </div>
            {getFeature(orderByFeatureName).isOn &&
              sortByItems.map((x, i) => (
                <div
                  key={x._key}
                  className={"col-8" + (i === 0 ? "" : " offset-4 py-1")}
                >
                  <div className="row align-items-center">
                    <div className="col">
                      <Dropdown
                        onChange={(x) => {
                          getFeature(orderByFeatureName).setFeatureValue(
                            ("orderby" + i.toString()) as "orderby0"
                          )(x);
                        }}
                        items={orderByFields}
                        optionLabel={TranslationService.translate.Select}
                        defaultValue={getOrderBy(model, i)}
                        readonly={
                          !getFeature(orderByFeatureName).isOn ||
                          !getFeature("includeinvoices").isOn
                        }
                      />
                    </div>
                    <div className="col-auto px-0">
                      <div className="d-flex align-items-center">
                        <i
                          onClick={() =>
                            getFeature(orderByFeatureName).setFeatureValue(
                              ("orderbyorderdesc" +
                                i.toString()) as "orderbyorderdesc0"
                            )(getOrderByDesc(model, i) === "on" ? "off" : "on")
                          }
                          className={
                            "fa-regular pointer fs-5 btnSort " +
                            (getOrderByDesc(model, i) === "on"
                              ? "fa-arrow-down-a-z"
                              : "fa-arrow-down-z-a")
                          }
                        ></i>
                      </div>
                    </div>
                    <div className="col-auto px-0">
                      <div className="delete">
                        <button
                          className="btn btnSort"
                          onClick={() => deleteSortBy(i)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            {getFeature(orderByFeatureName).isOn &&
              getFeature("includeinvoices").isOn &&
              sortByItems.length < 3 && (
                <div className="col-8 offset-4 py-1">
                  <button
                    className="btn btn-link d-flex gap-1 align-items-center fw-normal p-1 fs-7 "
                    onClick={() =>
                      setSortByItems((x) => [
                        ...x,
                        {
                          orderBy: undefined,
                          orderbyorderdesc: "off",
                          _key: v4()
                        }
                      ])
                    }
                  >
                    <i className="far fa-plus"></i>
                    {TranslationService.translate.AddOrder}
                  </button>
                </div>
              )}
            <div className="col-3 py-1 mb-2">
              <SwitchButton
                label={TranslationService.translate.ColumnTemplate}
                defaultValue={getFeature("reportexport").isOn}
                onChange={(x) =>
                  setTimeout(
                    () => getFeature("reportexport").toggleFeature(x),
                    5
                  )
                }
                disabled={!getFeature("includeinvoices").isOn}
              />
            </div>
            <div className="col-8 mb-2">
              {getFeature("reportexport").isOn && (
                <Dropdown
                  onChange={getFeature("reportexport").setFeatureValue(
                    "reportexport"
                  )}
                  items={invoiceReportExportOptions}
                  optionLabel={TranslationService.translate.SelectTemplate}
                  readonly={
                    !getFeature("reportexport").isOn ||
                    !getFeature("includeinvoices").isOn
                  }
                  defaultValue={defaultReportExportOption || undefined}
                />
              )}
            </div>
            <div className="col-3 py-1">
              <SwitchButton
                label={TranslationService.translate.HeaderLanguage}
                defaultValue={getFeature("language").isOn}
                onChange={(x) =>
                  setTimeout(() => getFeature("language").toggleFeature(x), 5)
                }
                disabled={!getFeature("includeinvoices").isOn}
              />
            </div>
            <div className="col-8">
              {getFeature("language").isOn && (
                <Dropdown
                  onChange={setEmail("language")}
                  items={languages}
                  readonly={!getFeature("includeinvoices").isOn}
                  defaultValue={model.item?.language}
                />
              )}
            </div>
            <div className="py-1"></div>
            <hr />
          </div>
        )}

        {CompanyService.getTags()?.length > 0 && (
          <div className="d-flex align-items-center mb-1">
            <div style={{ width: 45, textAlign: "center" }}>
              <TooltipComponent title={TranslationService.translate.Tags}>
                <i
                  className="fa fa-tags activity-icon-color my-2"
                  onClick={() => {}}
                />
              </TooltipComponent>
            </div>
            <div className=" col">
              <TagsDropdownEditor
                onChange={(x) =>
                  setEmail("Tag_Message")(
                    x.split(", ").map((y) => ({ TagID: parseInt(y) }))
                  )
                }
                defaultValue={model?.item?.Tag_Message?.map(
                  (x) => x.TagID
                ).join(", ")}
              />
            </div>
          </div>
        )}

        {hasValuesLength(activityTypeOptions) && (
          <div className="row align-items-center mb-2">
            <div className="col-2">
              <label className="form-label mb-0">
                {TranslationService.translate.ActivityType}
              </label>
            </div>
            <div className="col-10">
              <Dropdown
                onChange={requiredManager.makeRequiredIf(
                  CompanyService.getSetting("mandatoryemailtype"),
                  setEmail("activitytypeid"),
                  "activitytype"
                )}
                items={activityTypeOptions}
                optionLabel={TranslationService.translate.SelectActivityType}
                value={model.item?.activitytypeid}
                defaultValue={model.item?.activitytypeid}
              />

              {CompanyService.getSetting("mandatoryemailtype") && (
                <ValidationMessage
                  onChange={requiredManager.getValidation("activitytype")}
                  defaultValue={model.item?.activitytypeid}
                />
              )}
            </div>
          </div>
        )}
        {CompanyService.getGroupName()?.length > 0 && (
          <div className="row align-items-center mb-2">
            <label className="col-2 font-weight-normal col-form-label">
              {CompanyService.getGroupName()}
            </label>
            <div className="col-10">
              <AutocompleteSelect
                loadOptions={groupSelectOptions}
                onChange={(x) => setEmail("groupid")(x?.value)}
                defaultVal={
                  groupLabel && model.item?.groupid
                    ? {
                        value: model.item?.groupid!.toString(),
                        label: groupLabel
                          ? (groupLabel as string)
                          : model.item?.groupid!.toString()
                      }
                    : undefined
                }
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

type AddressBarProps = {
  model: Partial<EmailComposeModel>;
  setEmail: (email: keyof EmailComposeModel["item"]) => (value: string) => void;
  emailProp: keyof EmailComposeModel["item"];
  label: string;
  requiredManager?: RequiredManager;
};

const AddressBar = ({
  model,
  setEmail,
  emailProp,
  label,
  requiredManager
}: AddressBarProps) => {
  const onChange = useMemo(() => {
    const baseOnChange = setEmail(emailProp);
    return requiredManager
      ? requiredManager.makeRequiredWithId(baseOnChange, emailProp)
      : baseOnChange;
  }, [requiredManager, emailProp, setEmail]);
  const loadItems = useMemo(
    () => async (x: string) =>
      mapIfSuccess(
        await ClientService.getEmailsAutocomplete(model.item!.personid!, x),
        (x) => x.suggestions.map((y) => ({ text: y.data, value: y.value }))
      ),
    [model.item]
  );
  const data = model.item?.[emailProp] as string;
  const value =
    emailProp === "to" ? [...new Set(data.split(","))].join(",") : data;

  return (
    <div className="row mb-2 g-0 align-items-center">
      <div className="col-1">
        <label className="form-label mb-0">{label}</label>
      </div>
      <div className="col-11">
        {emailProp === "subject" ? (
          <TextEditor onChange={onChange} value={value} maxLength={300} />
        ) : (
          <AutocompleteAsync
            loadItems={loadItems}
            onChange={onChange}
            defaultValue={value}
          />
        )}
        {requiredManager && (
          <ValidationMessage onChange={onChange} defaultValue={value} />
        )}
      </div>
    </div>
  );
};

// async function cleanImagesFromContent(editor: TinyMCEEditor) {
//     const blobs = await editor._scanForImages();
//     for (const blob of blobs) {
//         if (blob.image.src.startsWith("blob")) {
//             blob.image.src = "data:" + blob.blobInfo.blob().type + ";base64," + blob.blobInfo.base64();
//         }
//     }
//     return editor.getContent();
// }

function getOrderBy(model: Partial<EmailComposeModel>, index: number) {
  return model?.item?.[("orderby" + index) as "orderby1"];
}
function getOrderByDesc(model: Partial<EmailComposeModel>, index: number) {
  return model?.item?.[("orderbyorderdesc" + index) as "orderbyorderdesc1"];
}

function getDefaultSortByItems(model: Partial<EmailComposeModel>) {
  const items = Array.from({ length: 3 })
    .map((x, i) => ({
      orderBy: getOrderBy(model, i),
      orderbyorderdesc: getOrderByDesc(model, i),
      _key: v4()
    }))
    .filter((x) => x.orderBy);
  if (items.length === 0) {
    items.push({ orderBy: undefined, orderbyorderdesc: "off", _key: v4() });
  }
  return items;
}
